/**
 * J1-J3 auto-play rules (docs/design/2026-10-11-auto-play.md).
 * - Zone rule: auto only in monster pockets, small-dungeon camps, dungeon trash and at a world boss; refused in duels,
 *   PvP, the arena, tournaments, the safe plaza and indoors. (Maps other than the 256 m plains have no pockets: there
 *   auto is allowed anywhere not refused.)
 * - Budget (autoBudget.ts): drains while auto is on, empty turns auto off with a notice.
 * - Yield: while auto is on, cultivation and drops are x0.6 with no rarity upgrade (`autoMul`, `autoOn`).
 * Real-day top-up: `rollDay` (called by dayReset.applyRealDay).
 */
import { drainAutoBudget, newAutoBudget, rollAutoBudget } from '../autoBudget';
import { POCKETS, PLAINS_SIZE } from '../data/layouts/plains';
import type { World } from '../world';
import { dungeonOf } from './dungeon';
import { duelMatchOf } from './duelMatch';
import { pvpDuelOf } from './pvpDuel';
import { safePlaceOf } from './safeZones';
import { smallDungeonState } from './smallDungeons';
import { worldBossOf } from './worldBosses';
import type { SystemHost, WorldSystem } from './types';

export const AUTO_YIELD = 0.6;
/** Metres past a pocket box or camp rim that still count as "in" (monsters roam and respawn there). */
export const AUTO_ZONE_MARGIN = 6;

export const AUTO_NOTICE = {
  refused: 'ออโต้ใช้ได้เฉพาะในแหล่งอสูร ค่ายสู้ ดันเจี้ยน และจอมมาร',
  duel: 'ออโต้ใช้ไม่ได้ในการประลองและสังเวียน',
  safe: 'ออโต้ใช้ไม่ได้ในลานปลอดภัย',
  empty: 'เวลาออโต้วันนี้หมดแล้ว ปิดออโต้',
  none: 'เวลาออโต้หมด รอรีเซ็ตตอน 05:00',
} as const;

const inBox = (b: readonly number[], x: number, y: number, m: number): boolean =>
  x >= b[0]! - m && x < b[1]! + m && y >= b[2]! - m && y < b[3]! + m;

export class AutoRulesSystem implements WorldSystem {
  readonly name = 'autoRules';
  /** Tests that stage a fight on open ground switch the zone and safe-plaza checks off (the bout and budget rules stay). */
  anywhere = false;

  constructor(private readonly h: SystemHost) {}

  /** The reason auto is refused here, or null when it may run. */
  refusal(w: World = this.h.world): string | null {
    const p = w.player;
    if (w.duel || w.inArena() || duelMatchOf(w)?.active || pvpDuelOf(w)?.active) return AUTO_NOTICE.duel;
    if (this.anywhere) return null;
    if (w.interiors.inside || safePlaceOf(w) !== null) return AUTO_NOTICE.safe;
    if (w.map.zone !== 'plains' || w.map.w !== PLAINS_SIZE) return null;
    if (dungeonOf(w)?.run) return null;
    const boss = worldBossOf(w)?.state();
    if (boss && Math.hypot(p.x - boss.arena.x, p.y - boss.arena.y) <= boss.arena.r + AUTO_ZONE_MARGIN) return null;
    for (const c of smallDungeonState(w))
      if (Math.hypot(p.x - c.x, p.y - c.y) <= c.radius + AUTO_ZONE_MARGIN) return null;
    for (const k of POCKETS) if (inBox(k.box, p.x, p.y, AUTO_ZONE_MARGIN)) return null;
    return AUTO_NOTICE.refused;
  }

  /** Called by World.setAuto before switching on: false (with a notice) when refused or the budget is empty. */
  allow(): boolean {
    const w = this.h.world;
    const why = this.refusal(w);
    if (why) {
      this.h.notice(why);
      return false;
    }
    const b = this.budget();
    if (b.secs <= 0) {
      this.h.notice(AUTO_NOTICE.none);
      return false;
    }
    return true;
  }

  /** The current player's budget, created on first use. */
  budget() {
    const pr = this.h.world.progress;
    return (pr.autoBudget ??= newAutoBudget(0));
  }

  /** Real-day top-up (dayReset.applyRealDay). A budget made before the day was known starts at this day. */
  realDay(day: number): void {
    this.rollDay(day);
  }
  rollDay(day: number): void {
    const b = this.h.world.progress.autoBudget;
    if (!b) {
      this.h.world.progress.autoBudget = newAutoBudget(day);
      return;
    }
    rollAutoBudget(b, day);
  }

  step(dt: number): void {
    const w = this.h.world;
    if (!w.auto.on) return;
    const left = drainAutoBudget(this.budget(), dt);
    const inBout = !w.inArena() && (!!duelMatchOf(w)?.active || !!pvpDuelOf(w)?.active);
    if (left <= 0 || inBout) {
      w.apply({ t: 'auto', on: false });
      this.h.notice(left <= 0 ? AUTO_NOTICE.empty : AUTO_NOTICE.duel);
    }
  }
}

/** Yield multiplier for the current player: 0.6 while auto is on, else 1. */
export const autoMul = (w: { auto: { on: boolean } }): number => (w.auto.on ? AUTO_YIELD : 1);
